import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  VisitTask,
  CreateMonitoringTaskRequest,
  UpdateMonitoringTaskRequest,
  UpdateTaskStatusRequest,
  MonitoringTaskListResponse,
  MonitoringTaskFilters,
  TaskStatistics,
} from "../pages/MonitoringTracker/types/monitoringVisit.types";
// API Request/Response types
export interface CreateMonitoringTaskApiRequest {
  visit_id: string;
  task_name: string;
  description?: string;
  due_date: string;
  /** Create CTMS Task (optional) */
  study_id?: string;
  site_id?: string;
  priority?: "High" | "Medium" | "Low";
  assigned_to_user_id?: string;
  source_module?: string;
  source_entity_id?: string;
  source_entity_type?: string;
}
export interface UpdateMonitoringTaskApiRequest {
  task_name?: string;
  description?: string;
  due_date?: string;
  status?: string;
}
export interface UpdateTaskStatusApiRequest {
  status: string;
}
export interface VisitTaskApiResponse {
  id: string;
  visitId?: string;
  visit_id?: string; // Handle both camelCase and snake_case
  taskName?: string;
  task_name?: string;
  description?: string;
  dueDate?: string;
  due_date?: string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  createdBy?: string;
  created_by?: string;
  priority?: string | null;
  assignedToUserId?: string | null;
  assigned_to_user_id?: string | null;
  /** When backend loads creator relation */
  creator?: {
    id?: string;
    email?: string;
    username?: string;
    userDetails?: {
      fullName?: string;
    };
  };
}
export interface MonitoringTaskListApiResponse {
  items: VisitTaskApiResponse[];
  total: number;
  page: number;
  limit: number;
}
export interface TaskStatisticsApiResponse {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
}
export interface IMonitoringTaskRepository {
  createTask(task: CreateMonitoringTaskRequest): Promise<VisitTask>;
  getTasks(
    page?: number,
    limit?: number,
    filters?: MonitoringTaskFilters,
  ): Promise<MonitoringTaskListResponse>;
  getTaskById(id: string): Promise<VisitTask>;
  updateTask(
    id: string,
    updates: UpdateMonitoringTaskRequest,
  ): Promise<VisitTask>;
  deleteTask(id: string): Promise<void>;
  updateTaskStatus(
    id: string,
    status: UpdateTaskStatusRequest,
  ): Promise<VisitTask>;
  getTasksByVisit(visitId: string): Promise<VisitTask[]>;
  getTaskStatisticsByVisit(visitId: string): Promise<TaskStatistics>;
  getOverdueTasks(): Promise<VisitTask[]>;
}
export class MonitoringTaskRepository implements IMonitoringTaskRepository {
  // Map backend status to frontend status
  private mapTaskStatus(
    status: string | undefined | null,
  ): "Pending" | "In Progress" | "Completed" {
    if (!status) {
      return "Pending"; // Default to Pending if status is missing
    }
    // Handle enum values directly (e.g., "Pending", "In Progress", "Completed")
    const enumValueMap: Record<
      string,
      "Pending" | "In Progress" | "Completed"
    > = {
      Pending: "Pending",
      "In Progress": "In Progress",
      Completed: "Completed",
    };
    if (enumValueMap[status]) {
      return enumValueMap[status];
    }
    // Handle enum keys (e.g., "PENDING", "IN_PROGRESS", "COMPLETED")
    const keyToValueMap: Record<
      string,
      "Pending" | "In Progress" | "Completed"
    > = {
      PENDING: "Pending",
      IN_PROGRESS: "In Progress",
      COMPLETED: "Completed",
    };
    return keyToValueMap[status.toUpperCase()] ?? "Pending";
  }
  // Map frontend status to backend status
  private mapStatusToApi(
    status: "Pending" | "In Progress" | "Completed",
  ): string {
    const statusMap: Record<string, string> = {
      Pending: "PENDING",
      "In Progress": "IN_PROGRESS",
      Completed: "COMPLETED",
    };
    return statusMap[status] ?? "PENDING";
  }
  // Transform API response to frontend format (handles camelCase, snake_case, and nested response)
  private transformApiResponse(
    apiResponse: VisitTaskApiResponse | Record<string, unknown>,
  ): VisitTask {
    const r = apiResponse as Record<string, unknown>;
    const visitId = (r.visitId ?? r.visit_id ?? "") as string;
    const taskName = (r.taskName ?? r.task_name ?? "") as string;
    const description = r.description as string | undefined;
    const dueDate = (r.dueDate ?? r.due_date ?? "") as string;
    const status = r.status as string | undefined;
    const createdAt = (r.createdAt ??
      r.created_at ??
      new Date().toISOString()) as string;
    const updatedAt = (r.updatedAt ??
      r.updated_at ??
      new Date().toISOString()) as string;
    const createdBy = (r.createdBy ?? r.created_by ?? "") as string;
    const priority = (r.priority ?? undefined) as
      VisitTask["priority"] | undefined;
    const assignedToUserId = (r.assignedToUserId ??
      r.assigned_to_user_id ??
      undefined) as string | undefined;
    return {
      id: (r.id as string) ?? "",
      visitId,
      taskName,
      description,
      dueDate,
      status: this.mapTaskStatus(status),
      createdAt,
      updatedAt,
      createdBy,
      priority:
        priority && ["High", "Medium", "Low"].includes(priority)
          ? priority
          : undefined,
      assignedToUserId: assignedToUserId ?? undefined,
    };
  }
  async createTask(task: CreateMonitoringTaskRequest): Promise<VisitTask> {
    const apiRequest: CreateMonitoringTaskApiRequest = {
      visit_id: task.visitId,
      task_name: task.taskName,
      due_date: task.dueDate,
    };
    if (task.description !== null && task.description !== "") {
      apiRequest.description = task.description;
    }
    if (task.studyId !== null && task.studyId !== "") {
      apiRequest.study_id = task.studyId;
    }
    if (task.siteId !== null && task.siteId !== "") {
      apiRequest.site_id = task.siteId;
    }
    if (task.priority !== null && task.priority !== undefined) {
      apiRequest.priority = task.priority;
    }
    if (task.assignedToUserId !== null && task.assignedToUserId !== "") {
      apiRequest.assigned_to_user_id = task.assignedToUserId;
    }
    if (task.sourceModule !== null && task.sourceModule !== "") {
      apiRequest.source_module = task.sourceModule;
    }
    if (task.sourceEntityId !== null && task.sourceEntityId !== "") {
      apiRequest.source_entity_id = task.sourceEntityId;
    }
    if (task.sourceEntityType !== null && task.sourceEntityType !== "") {
      apiRequest.source_entity_type = task.sourceEntityType;
    }
    const response: ApiResponse<
      | VisitTaskApiResponse
      | {
          data?: VisitTaskApiResponse;
        }
    > = await httpCoreService.post("/monitoring-tasks", apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to create monitoring task",
      );
    }
    const raw = response.data;
    const taskData =
      raw && typeof raw === "object" && "data" in raw && raw.data !== null
        ? (
            raw as {
              data: VisitTaskApiResponse;
            }
          ).data
        : raw;
    if (!taskData || typeof taskData !== "object") {
      throw new Error(
        "Monitoring task creation succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(taskData);
  }
  async getTasks(
    page = 1,
    limit = 10,
    filters?: MonitoringTaskFilters,
  ): Promise<MonitoringTaskListResponse> {
    const params: Record<string, string | number> = {
      page,
      limit,
    };
    // Add filter parameters if provided
    if (filters) {
      if (filters.visitId) {
        params.visit_id = filters.visitId;
      }
      if (filters.status) {
        params.status = this.mapStatusToApi(filters.status);
      }
      if (filters.dueFrom) {
        params.due_from = filters.dueFrom;
      }
      if (filters.dueTo) {
        params.due_to = filters.dueTo;
      }
      if (filters.createdBy) {
        params.created_by = filters.createdBy;
      }
    }
    const response: ApiResponse<MonitoringTaskListApiResponse> =
      await httpCoreService.get<MonitoringTaskListApiResponse>(
        "/monitoring-tasks",
        {
          params,
        },
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch monitoring tasks",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    // Response data contains { items, total, page, limit }
    const listData = response.data;
    // Validate that listData has the expected structure
    if (!listData || typeof listData !== "object") {
      throw new Error("Invalid response data structure from server");
    }
    // Ensure items is an array
    const items = listData.items || [];
    if (!Array.isArray(items)) {
      throw new Error("Response items is not an array");
    }
    return {
      tasks: items.map((item) => this.transformApiResponse(item)),
      total: listData.total || 0,
      page: listData.page || page,
      limit: listData.limit || limit,
    };
  }
  async getTaskById(id: string): Promise<VisitTask> {
    const response: ApiResponse<VisitTaskApiResponse> =
      await httpCoreService.get<VisitTaskApiResponse>(
        `/monitoring-tasks/${id}`,
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch monitoring task",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async updateTask(
    id: string,
    updates: UpdateMonitoringTaskRequest,
  ): Promise<VisitTask> {
    const apiRequest: UpdateMonitoringTaskApiRequest = {
      task_name: updates.taskName,
      description: updates.description,
      due_date: updates.dueDate,
      status: updates.status ? this.mapStatusToApi(updates.status) : undefined,
    };
    const response: ApiResponse<VisitTaskApiResponse> =
      await httpCoreService.put<
        VisitTaskApiResponse,
        UpdateMonitoringTaskApiRequest
      >(`/monitoring-tasks/${id}`, apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to update monitoring task",
      );
    }
    if (!response.data) {
      throw new Error(
        "Monitoring task update succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async deleteTask(id: string): Promise<void> {
    const response: ApiResponse<null> = await httpCoreService.delete<null>(
      `/monitoring-tasks/${id}`,
    );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to delete monitoring task",
      );
    }
  }
  async updateTaskStatus(
    id: string,
    statusUpdate: UpdateTaskStatusRequest,
  ): Promise<VisitTask> {
    // Map status to backend format (PENDING, IN_PROGRESS, COMPLETED)
    // Backend uses @Body('status') which expects { status: "PENDING" } format
    const backendStatus = this.mapStatusToApi(statusUpdate.status);
    // The PATCH /status endpoint expects { status: "PENDING" } in the request body
    const apiRequest: UpdateTaskStatusApiRequest = {
      status: backendStatus,
    };
    const response: ApiResponse<VisitTaskApiResponse> =
      await httpCoreService.patch<
        VisitTaskApiResponse,
        UpdateTaskStatusApiRequest
      >(`/monitoring-tasks/${id}/status`, apiRequest);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to update task status",
      );
    }
    if (!response.data) {
      throw new Error(
        "Task status update succeeded but no data was returned from the server.",
      );
    }
    return this.transformApiResponse(response.data);
  }
  async getTasksByVisit(visitId: string): Promise<VisitTask[]> {
    const response: ApiResponse<
      | VisitTaskApiResponse[]
      | {
          data?: unknown[];
          tasks?: unknown[];
        }
    > = await httpCoreService.get(`/monitoring-tasks/visit/${visitId}`);
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch visit tasks",
      );
    }
    const raw = response.data;
    interface WrappedVisitTasks {
      data?: VisitTaskApiResponse[];
      tasks?: VisitTaskApiResponse[];
    }
    const wrapped = raw as
      VisitTaskApiResponse[] | WrappedVisitTasks | undefined;
    const tasksData = Array.isArray(wrapped)
      ? wrapped
      : wrapped && typeof wrapped === "object"
        ? (wrapped.data ?? wrapped.tasks ?? [])
        : [];
    if (!Array.isArray(tasksData)) {
      return [];
    }
    return tasksData.map((item) => this.transformApiResponse(item));
  }
  async getTaskStatisticsByVisit(visitId: string): Promise<TaskStatistics> {
    const response: ApiResponse<TaskStatisticsApiResponse> =
      await httpCoreService.get<TaskStatisticsApiResponse>(
        `/monitoring-tasks/visit/${visitId}/stats`,
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch task statistics",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    // Response data contains task statistics (support both camelCase and snake_case from API)
    const d = response.data;
    return {
      total: d.total ?? 0,
      pending: d.pending ?? 0,
      inProgress:
        d.inProgress ?? (d as { in_progress?: number }).in_progress ?? 0,
      completed: d.completed ?? 0,
    };
  }
  async getOverdueTasks(): Promise<VisitTask[]> {
    const response: ApiResponse<VisitTaskApiResponse[]> =
      await httpCoreService.get<VisitTaskApiResponse[]>(
        "/monitoring-tasks/overdue",
      );
    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch overdue tasks",
      );
    }
    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }
    // Response data is array of tasks
    const tasksData = response.data;
    return Array.isArray(tasksData)
      ? tasksData.map((item) => this.transformApiResponse(item))
      : [];
  }
}
